import type {
  ApiOrder,
  PlaceOrderBillingInput,
  PlaceOrderDeliveryInput,
  PlaceOrderItemInput,
} from '../api/types'
import {
  CANCELLABLE_ORDER_STATUSES,
  CHECKOUT_UI,
  ONLINE_PAYMENT_WINDOW_MS,
  ORDER_CANCEL_WINDOW_MS,
  ORDER_FILTER,
  ORDER_FILTER_STATUSES,
  ORDER_PROGRESS,
  ORDER_STATUS,
  ORDER_STATUS_LABEL,
  ORDER_UI,
  PAYMENT_STATUS,
  PAYMENT_STATUS_LABEL,
  type CheckoutFormState,
  type OrderFilter,
  type OrderPlacedState,
  type OrderStatus,
  type PaymentStatus,
} from '../constants/orders'
import { PAYMENT_METHOD } from '../constants/settings'
import { toE164Phone } from './auth'
import type { CartLine } from './cart'
import { formatScheduledDelivery } from './schedule'

export function isOrderStatus(value: unknown): value is OrderStatus {
  return Object.values(ORDER_STATUS).includes(value as OrderStatus)
}

export function isPaymentStatus(value: unknown): value is PaymentStatus {
  return Object.values(PAYMENT_STATUS).includes(value as PaymentStatus)
}

export function orderStatusLabel(status: OrderStatus): string {
  return ORDER_STATUS_LABEL[status]
}

export function paymentStatusLabel(status: PaymentStatus): string {
  return PAYMENT_STATUS_LABEL[status]
}

export function isCancellableOrderStatus(status: OrderStatus): boolean {
  return CANCELLABLE_ORDER_STATUSES.includes(status)
}

export function isWithinOrderCancelWindow(createdAt: string, now = Date.now()): boolean {
  const placedAt = Date.parse(createdAt)
  if (Number.isNaN(placedAt)) return false
  return now - placedAt <= ORDER_CANCEL_WINDOW_MS
}

export function canCancelOrder(
  order: { status: OrderStatus; createdAt: string },
  now = Date.now(),
): boolean {
  return isCancellableOrderStatus(order.status) && isWithinOrderCancelWindow(order.createdAt, now)
}

export function checkoutDeliveryAddress(details: string, area: string): string {
  const line = details.trim()
  const main = area.trim()
  if (line && main && line.toLowerCase().includes(main.toLowerCase())) return line
  if (line && main) return `${line}, ${main}`
  return line || main
}

export function isUnpaidOnlineOrder(order: ApiOrder): boolean {
  return (
    order.paymentMethod === PAYMENT_METHOD.online &&
    order.paymentStatus === PAYMENT_STATUS.unpaid &&
    order.status !== ORDER_STATUS.cancelled
  )
}

export function onlinePaymentDeadline(createdAt: string): number | null {
  const placedAt = Date.parse(createdAt)
  if (Number.isNaN(placedAt)) return null
  return placedAt + ONLINE_PAYMENT_WINDOW_MS
}

export function onlinePaymentTimeoutMs(createdAt: string, now = Date.now()): number {
  const deadline = onlinePaymentDeadline(createdAt)
  if (deadline === null) return 0
  return Math.max(0, deadline - now)
}

export function isUnpaidOnlinePaymentExpired(order: ApiOrder, now = Date.now()): boolean {
  if (!isUnpaidOnlineOrder(order)) return false
  if (!isCancellableOrderStatus(order.status)) return false
  const deadline = onlinePaymentDeadline(order.createdAt)
  if (deadline === null) return false
  return now > deadline
}

export function nationalPhoneDigits(phone: string | null | undefined): string {
  if (!phone) return ''
  const digits = phone.replace(/\D/g, '')
  if (digits.length >= 10) return digits.slice(-10)
  return digits
}

export function cartLinesToOrderItems(lines: CartLine[]): PlaceOrderItemInput[] {
  return lines.map((line) => ({
    productId: line.productId,
    variantId: line.variantId,
    quantity: line.quantity,
    ...(line.wrapperId ? { wrapperId: line.wrapperId } : {}),
    ...(line.addonIds.length > 0 ? { addonIds: line.addonIds } : {}),
  }))
}

export function buildPlaceOrderDelivery(input: {
  recipientName: string
  recipientPhone: string
  address: string
  city: string
  pincode: string
  notes: string
  lat?: number
  lng?: number
}): PlaceOrderDeliveryInput {
  const pincode = input.pincode.trim()
  const notes = input.notes.trim()
  return {
    recipientName: input.recipientName.trim(),
    recipientPhone: toE164Phone(input.recipientPhone),
    address: input.address.trim(),
    city: input.city.trim(),
    ...(pincode ? { pincode } : {}),
    ...(notes ? { notes } : {}),
    ...(typeof input.lat === 'number' ? { lat: input.lat } : {}),
    ...(typeof input.lng === 'number' ? { lng: input.lng } : {}),
  }
}

export function isCheckoutAddressComplete(input: {
  name: string
  phone: string
  address: string
  city: string
  pincode: string
}): boolean {
  return (
    input.name.trim().length > 0 &&
    nationalPhoneDigits(input.phone).length === 10 &&
    input.address.trim().length > 0 &&
    input.city.trim().length > 0 &&
    input.pincode.trim().length > 0
  )
}

export function isCheckoutFormReady(
  form: CheckoutFormState,
  city: string | null | undefined,
): boolean {
  const shippingComplete = isCheckoutAddressComplete({
    name: form.recipientName,
    phone: form.recipientPhone,
    address: form.address,
    city: city ?? '',
    pincode: form.pincode,
  })
  const billingComplete =
    form.billingSameAsShipping ||
    isCheckoutAddressComplete({
      name: form.billingName,
      phone: form.billingPhone,
      address: form.billingAddress,
      city: form.billingCity,
      pincode: form.billingPincode,
    })
  return shippingComplete && billingComplete
}

export function checkoutPlacedTotal(orders: ApiOrder[]): number {
  return orders.reduce((sum, order) => sum + order.total, 0)
}

export function isBillingFormEmpty(
  input: Pick<
    CheckoutFormState,
    | 'billingName'
    | 'billingPhone'
    | 'billingAddress'
    | 'billingCity'
    | 'billingPincode'
  >,
): boolean {
  return (
    !input.billingName.trim() &&
    !input.billingPhone.trim() &&
    !input.billingAddress.trim() &&
    !input.billingCity.trim() &&
    !input.billingPincode.trim()
  )
}

export function buildPlaceOrderBilling(input: {
  sameAsShipping: boolean
  name: string
  phone: string
  address: string
  city: string
  pincode: string
}): PlaceOrderBillingInput {
  if (input.sameAsShipping) return { sameAsShipping: true }
  const pincode = input.pincode.trim()
  return {
    sameAsShipping: false,
    name: input.name.trim(),
    phone: toE164Phone(input.phone),
    address: input.address.trim(),
    city: input.city.trim(),
    ...(pincode ? { pincode } : {}),
  }
}

export function prependOrder(orders: ApiOrder[] | undefined, order: ApiOrder): ApiOrder[] {
  return prependOrders(orders, [order])
}

export function prependOrders(
  orders: ApiOrder[] | undefined,
  next: ApiOrder[],
): ApiOrder[] {
  const ids = new Set(next.map((order) => order.id))
  return [...next, ...(orders ?? []).filter((order) => !ids.has(order.id))]
}

export function replaceOrder(orders: ApiOrder[] | undefined, order: ApiOrder): ApiOrder[] {
  return (orders ?? []).map((item) => (item.id === order.id ? order : item))
}

export function isOrderPlacedState(state: unknown): boolean {
  return Boolean(
    state &&
      typeof state === 'object' &&
      'placed' in state &&
      (state as { placed?: boolean }).placed,
  )
}

export function readOrderPlacedState(state: unknown): OrderPlacedState | null {
  if (!isOrderPlacedState(state)) return null
  const record = state as OrderPlacedState
  return {
    placed: true,
    checkoutId: record.checkoutId,
    orderIds: Array.isArray(record.orderIds) ? record.orderIds : undefined,
  }
}

export function formatDeliveryLine(delivery: {
  address: string
  city: string
  pincode: string | null
}): string {
  const address = delivery.address.trim()
  const city = delivery.city.trim()
  const pincode = delivery.pincode?.trim() ?? ''
  const parts = [address]
  if (city && !address.toLowerCase().includes(city.toLowerCase())) {
    parts.push(city)
  }
  if (pincode && !address.includes(pincode)) parts.push(pincode)
  return parts.filter(Boolean).join(', ')
}

export function couponApplyMessage(code: string): string {
  if (!code.trim()) return CHECKOUT_UI.couponEmpty
  return CHECKOUT_UI.couponInvalid
}

export function deliveryPromiseLabel(order: {
  isExpress?: boolean
  scheduledFor?: string | null
}): string {
  if (order.scheduledFor) return formatScheduledDelivery(order.scheduledFor)
  if (order.isExpress) return ORDER_UI.expressPromise
  return ORDER_UI.standardPromise
}

export function orderProgressIndex(status: OrderStatus): number {
  return ORDER_PROGRESS.indexOf(status)
}

export function orderItemAddonLabel(names: string[]): string | null {
  const cleaned = names.map((name) => name.trim()).filter(Boolean)
  return cleaned.length > 0 ? cleaned.join(', ') : null
}

export function orderItemCountLabel(order: ApiOrder): string {
  const count = order.items.reduce((sum, item) => sum + item.quantity, 0)
  return `${count} ${count === 1 ? ORDER_UI.listCountSingular : ORDER_UI.listCountPlural}`
}

export function isOrderFilter(value: unknown): value is OrderFilter {
  return Object.values(ORDER_FILTER).includes(value as OrderFilter)
}

export function matchesOrderFilter(order: ApiOrder, filter: OrderFilter): boolean {
  if (filter === ORDER_FILTER.all) return true
  return ORDER_FILTER_STATUSES[filter].includes(order.status)
}

export function matchesOrderSearch(order: ApiOrder, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const haystack = [
    order.orderNumber,
    order.delivery.recipientName,
    ...order.items.flatMap((item) => [item.productName, item.variantLabel]),
  ]
  return haystack.some((value) => value?.toLowerCase().includes(needle))
}

export function orderFilterCounts(orders: ApiOrder[]): Record<OrderFilter, number> {
  const counts = Object.fromEntries(
    Object.values(ORDER_FILTER).map((filter) => [filter, 0]),
  ) as Record<OrderFilter, number>
  for (const order of orders) {
    for (const filter of Object.values(ORDER_FILTER)) {
      if (matchesOrderFilter(order, filter)) counts[filter] += 1
    }
  }
  return counts
}

export function orderListCountLabel(shown: number, total: number): string {
  const noun = total === 1 ? ORDER_UI.listCountSingular : ORDER_UI.listCountPlural
  if (shown === total) return `${total} ${noun}`
  return `${shown} ${ORDER_UI.listCountOf} ${total} ${noun}`
}

export function orderPreviewItems(
  order: ApiOrder,
): Array<{ id: string; url: string | null }> {
  return order.items.slice(0, 3).map((item) => ({
    id: item.id,
    url: item.imageUrl,
  }))
}
